import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  Textarea,
} from "@evolua/ui";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useApproveManagerApproval,
  useBatchManagerApprovals,
  useManagerApprovals,
  useRejectManagerApproval,
  useSyncManagerVacations,
} from "../api/use-manager.js";

/** Fila de aprovações do gestor (FASE-06-T44 / T47). */
export function ApprovalsPage() {
  const query = useManagerApprovals();
  const approve = useApproveManagerApproval();
  const reject = useRejectManagerApproval();
  const batch = useBatchManagerApprovals();
  const syncVacations = useSyncManagerVacations();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [staleBlock, setStaleBlock] = useState<string | null>(null);
  const [coverageBlock, setCoverageBlock] = useState<string | null>(null);
  const [pendingRetryId, setPendingRetryId] = useState<string | null>(null);
  const [pendingForceId, setPendingForceId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function errorMessage(err: unknown): string {
    if (err instanceof EvoluaApiError) return err.problem.detail ?? err.message;
    if (err instanceof Error) return err.message;
    return "Falha na decisão";
  }

  function isStaleError(err: unknown): boolean {
    return err instanceof EvoluaApiError && err.problem.code === "VACATION_APPROVE_STALE";
  }

  function isCoverageError(err: unknown): boolean {
    return (
      err instanceof EvoluaApiError && err.problem.code === "MANAGER_VACATION_COVERAGE"
    );
  }

  async function onApprove(id: string, opts: { force?: boolean } = {}) {
    setActionError(null);
    setStaleBlock(null);
    setCoverageBlock(null);
    setBusyId(id);
    try {
      await approve.mutateAsync({
        id,
        comment: "Aprovado pelo gestor",
        ...(opts.force ? { force: true } : {}),
      });
      setPendingRetryId(null);
      setPendingForceId(null);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      if (isStaleError(err)) {
        setStaleBlock(errorMessage(err));
        setPendingRetryId(id);
      } else if (isCoverageError(err)) {
        setCoverageBlock(errorMessage(err));
        setPendingForceId(id);
      } else {
        setActionError(errorMessage(err));
      }
    } finally {
      setBusyId(null);
    }
  }

  async function onSyncAndRetry() {
    setActionError(null);
    try {
      const synced = await syncVacations.mutateAsync();
      setStaleBlock(null);
      if (pendingRetryId) {
        await onApprove(pendingRetryId);
      } else {
        setActionError(null);
        setStaleBlock(`Saldo atualizado (${synced.label}). Pode aprovar novamente.`);
      }
    } catch (err) {
      setActionError(errorMessage(err));
    }
  }

  async function onRejectConfirm() {
    if (!rejectId) return;
    setActionError(null);
    setBusyId(rejectId);
    try {
      await reject.mutateAsync({ id: rejectId, comment: rejectComment.trim() });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(rejectId);
        return next;
      });
      setRejectId(null);
      setRejectComment("");
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onBatchApprove(opts: { force?: boolean } = {}) {
    if (selected.size === 0) return;
    setActionError(null);
    setStaleBlock(null);
    setCoverageBlock(null);
    try {
      await batch.mutateAsync({
        items: [...selected].map((id) => ({
          id,
          decision: "APPROVE" as const,
          comment: "Aprovado em lote",
          ...(opts.force ? { force: true } : {}),
        })),
      });
      setSelected(new Set());
      setPendingForceId(null);
    } catch (err) {
      if (isStaleError(err)) {
        setStaleBlock(errorMessage(err));
        setPendingRetryId([...selected][0] ?? null);
      } else if (isCoverageError(err)) {
        setCoverageBlock(errorMessage(err));
        setPendingForceId([...selected][0] ?? null);
      } else {
        setActionError(errorMessage(err));
      }
    }
  }

  if (query.isPending) {
    return (
      <div className="evolua-stack evolua-stack--2" aria-busy="true">
        <Skeleton height="2rem" />
        <Skeleton height="8rem" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    const err = query.error;
    const correlationId =
      err instanceof EvoluaApiError
        ? (err.problem.traceId ?? err.problem.code)
        : undefined;
    const description =
      err instanceof EvoluaApiError
        ? (err.problem.detail ?? err.message)
        : err instanceof Error
          ? err.message
          : undefined;
    return (
      <ErrorState
        onRetry={() => void query.refetch()}
        {...(description ? { description } : {})}
        {...(correlationId ? { correlationId } : {})}
      />
    );
  }

  const items = query.data.data;

  return (
    <>
      <PageHeader
        title="Aprovações"
        lead="Fila única da sua equipe. Reprovação exige justificativa; decisões são auditadas e notificadas ao solicitante. Aprovação de férias exige saldo atualizado e respeita cobertura mínima da equipe."
      />

      {staleBlock ? (
        <Card
          title="Saldo de férias desatualizado"
          className="evolua-stack evolua-stack--3"
        >
          <Alert tone="warning">{staleBlock}</Alert>
          <Button
            type="button"
            disabled={syncVacations.isPending || busyId !== null}
            onClick={() => void onSyncAndRetry()}
          >
            {syncVacations.isPending
              ? "Sincronizando…"
              : pendingRetryId
                ? "Sincronizar saldo e aprovar"
                : "Sincronizar saldo de férias"}
          </Button>
        </Card>
      ) : null}

      {coverageBlock ? (
        <Card title="Cobertura da equipe" className="evolua-stack evolua-stack--3">
          <Alert tone="warning">{coverageBlock}</Alert>
          <p className="evolua-muted">
            <Link to="/calendario-ferias">Ver calendário de férias</Link> antes de forçar.
          </p>
          <div className="evolua-stack evolua-stack--3">
            {pendingForceId ? (
              <Button
                type="button"
                disabled={busyId !== null || approve.isPending}
                onClick={() => void onApprove(pendingForceId, { force: true })}
              >
                Forçar aprovação
              </Button>
            ) : null}
            {selected.size > 0 ? (
              <Button
                type="button"
                variant="secondary"
                disabled={batch.isPending}
                onClick={() => void onBatchApprove({ force: true })}
              >
                Forçar lote selecionado
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCoverageBlock(null);
                setPendingForceId(null);
              }}
            >
              Cancelar
            </Button>
          </div>
        </Card>
      ) : null}

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      {items.length === 0 ? (
        <EmptyState
          title="Nada pendente"
          description="Quando houver solicitações da sua equipe, elas aparecem aqui."
        />
      ) : (
        <>
          <div className="evolua-stack evolua-stack--3">
            <strong>{query.data.total} pendente(s)</strong>
            <Button
              type="button"
              variant="secondary"
              disabled={selected.size === 0 || batch.isPending}
              onClick={() => void onBatchApprove()}
            >
              Aprovar selecionadas ({selected.size})
            </Button>
            <Link to="/calendario-ferias">Calendário →</Link>
            {" · "}
            <Link to="/">Voltar ao dashboard →</Link>
          </div>

          <Table
            caption="Aprovações pendentes"
            getRowId={(item) => item.id}
            columns={[
              {
                id: "sel",
                header: "Sel.",
                cell: (item) => (
                  <Checkbox
                    label={`Selecionar ${item.label}`}
                    checked={selected.has(item.id)}
                    onChange={() => toggle(item.id)}
                  />
                ),
              },
              { id: "label", header: "Pedido", cell: (item) => item.label },
              { id: "who", header: "Solicitante", cell: (item) => item.requesterName },
              {
                id: "meta",
                header: "SLA",
                cell: (item) =>
                  `${item.type}${
                    item.dueAt ? ` · ${new Date(item.dueAt).toLocaleString("pt-BR")}` : ""
                  }`,
              },
              {
                id: "actions",
                header: "Ações",
                cell: (item) => (
                  <div className="evolua-stack evolua-stack--3">
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === item.id || approve.isPending}
                      onClick={() => void onApprove(item.id)}
                    >
                      Aprovar
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === item.id}
                      onClick={() => {
                        setRejectId(item.id);
                        setRejectComment("");
                        setActionError(null);
                      }}
                    >
                      Reprovar
                    </Button>
                    <Link to={`/equipe/${item.employeeId}`}>Ver colaborador →</Link>
                  </div>
                ),
              },
            ]}
            rows={items}
          />
        </>
      )}

      <Modal
        open={Boolean(rejectId)}
        title="Justificativa da reprovação"
        onClose={() => {
          setRejectId(null);
          setRejectComment("");
        }}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setRejectId(null);
                setRejectComment("");
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={!rejectComment.trim() || reject.isPending}
              onClick={() => void onRejectConfirm()}
            >
              Confirmar reprovação
            </Button>
          </>
        }
      >
        <p>A justificativa é obrigatória e fica na trilha de auditoria.</p>
        <Textarea
          label="Motivo da reprovação"
          value={rejectComment}
          onChange={(e) => setRejectComment(e.target.value)}
          rows={3}
          placeholder="Motivo da reprovação"
        />
      </Modal>
    </>
  );
}
